import { useState, useMemo, useEffect, useCallback } from 'react';
import { Route, SearchFilters, OrderByOption } from '../types';
import { isGradeInRange, parseLocation, parseGrade } from '../utils';
import { groupRoutesByLocation } from '../utils/locationUtils';

const SEARCH_FILTERS_KEY = 'rrg_search_filters';

export function useSearch(routes: Route[]) {
  // Load filters from localStorage on initialization
  const [filters, setFilters] = useState<SearchFilters>(() => {
    const saved = localStorage.getItem(SEARCH_FILTERS_KEY);
    return saved ? JSON.parse(saved) : {};
  });
  
  // Search term is not persisted - starts fresh on each page load
  const [searchTerm, setSearchTerm] = useState('');
  
  // Debounced search term for actual filtering
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(SEARCH_FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);

  // Debounce search term updates (1 second delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when sort order changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.orderBy]);

  // Sort routes based on orderBy preference
  const sortRoutes = useCallback((routes: Route[], orderBy?: OrderByOption): Route[] => {
    // Always create a copy to avoid mutating the original array
    return [...routes].sort((a, b) => {
      switch (orderBy) {
        case 'quality':
          const qualityDiff = b['Avg Stars'] - a['Avg Stars'];
          if (qualityDiff !== 0) return qualityDiff;
          return String(a.Route || '').localeCompare(String(b.Route || ''));
        
        case 'grade':
          const gradeDiff = parseGrade(a.Rating) - parseGrade(b.Rating);
          if (gradeDiff !== 0) return gradeDiff;
          return String(a.Route || '').localeCompare(String(b.Route || ''));
        
        case 'length':
          const lengthDiff = a.Length - b.Length;
          if (lengthDiff !== 0) return lengthDiff;
          return String(a.Route || '').localeCompare(String(b.Route || ''));
        
        case 'name':
          return String(a.Route || '').localeCompare(String(b.Route || ''));
        
        default:
          // Default sorting: quality descending, then by route name
          const defaultQualityDiff = b['Avg Stars'] - a['Avg Stars'];
          if (defaultQualityDiff !== 0) return defaultQualityDiff;
          return String(a.Route || '').localeCompare(String(b.Route || ''));
      }
    });
  }, []);

  // Filter and sort routes based on search term and filters
  const allFilteredRoutes = useMemo(() => {
    let results = routes;

    // Apply text search with exact "contains" matching
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      results = routes.filter(route => {
        return (
          route.Route.toLowerCase().includes(searchLower) ||
          route.Location.toLowerCase().includes(searchLower) ||
          String(route.Rating).toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply filters
    const filtered = results.filter(route => {
      // Length filter
      if (filters.minLength !== undefined && route.Length < filters.minLength) return false;
      if (filters.maxLength !== undefined && route.Length > filters.maxLength) return false;

      // Quality filter (Avg Stars) - only minimum quality
      if (filters.minQuality !== undefined && route['Avg Stars'] < filters.minQuality) return false;

      // Grade range filter
      const gradeInRange = isGradeInRange(route.Rating, filters.minGrade, filters.maxGrade);
      if (!gradeInRange) return false;

      // Route type filters (All when none selected)
      if (filters.trad === true && route['Route Type'] !== 'Trad') return false;
      if (filters.sport === true && route['Route Type'] !== 'Sport') return false;
      if (filters.boulder === true && route['Route Type'] !== 'Boulder') return false;

      // Pitches filter
      if (filters.minPitches !== undefined && route.Pitches < filters.minPitches) return false;
      if (filters.maxPitches !== undefined && route.Pitches > filters.maxPitches) return false;

      // Region and crag filters
      const locationInfo = parseLocation(route.Location);
      if (filters.region && (!locationInfo || locationInfo.region !== filters.region)) return false;
      if (filters.crag && (!locationInfo || locationInfo.crag !== filters.crag)) return false;

      return true;
    });

    // Sort routes based on orderBy preference
    return sortRoutes(filtered, filters.orderBy);
  }, [routes, debouncedSearchTerm, filters, sortRoutes]);

  // Get paginated routes first
  const paginatedRoutes = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allFilteredRoutes.slice(startIndex, endIndex);
  }, [allFilteredRoutes, currentPage, pageSize]);

  // Group the paginated routes by location
  const groupedRoutes = useMemo(() => {
    return groupRoutesByLocation(paginatedRoutes);
  }, [paginatedRoutes]);

  // Calculate pagination info based on routes
  const totalPages = Math.ceil(allFilteredRoutes.length / pageSize);

  const updateFilters = (newFilters: Partial<SearchFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    // Reset to first page when filters change (including sort order)
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setCurrentPage(1);
    // Clear localStorage
    localStorage.removeItem(SEARCH_FILTERS_KEY);
  };

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const nextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const prevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  return {
    searchTerm,
    setSearchTerm,
    filters,
    updateFilters,
    clearFilters,
    filteredRoutes: allFilteredRoutes, // Keep for backward compatibility
    groupedRoutes,
    totalResults: allFilteredRoutes.length,
    totalRoutes: routes.length,
    currentPage,
    totalPages,
    pageSize,
    goToPage,
    nextPage,
    prevPage,
  };
}
